import { useState, useCallback, useEffect, useRef, useMemo } from 'react';
import { debounce } from 'lodash';
import BigNumber from 'bignumber.js';
import { MIN_VALUES } from '../../../constants/minValues';
import RustCalculatorApi from '../../../api/rustApi';
import UserSettingsApi from '../../../api/userSettingsApi';
import { FOREX_PAIRS, DEFAULT_PAIR } from '../config/pairs.ts';
import type { PairSpec } from '../config/pairs.ts';
import type { CalculatorRequest, RiskUnit } from '../../../types/calculator';
import { computeMultiExits, defaultTpExits } from '../../../utils/multiExits';

const CALCULATION_DELAY = 100;
const LOCALSTORAGE_DEBOUNCE = 500;
const API_SYNC_DEBOUNCE = 2000;
const LOCALSTORAGE_KEY = 'forexCalculatorSettings';

BigNumber.config({
    EXPONENTIAL_AT: 1e+9,
    DECIMAL_PLACES: 8,
    ROUNDING_MODE: BigNumber.ROUND_DOWN,
    FORMAT: {
        decimalSeparator: '.',
        groupSeparator: '',
        groupSize: 0,
        secondaryGroupSize: 0,
        fractionGroupSeparator: '',
        fractionGroupSize: 0
    }
});

const defaultValues = {
    pair: DEFAULT_PAIR,
    risk: new BigNumber(10),
    riskUnit: 'fixed',
    accountBalance: new BigNumber(25000),
    nominalRR: new BigNumber(2),
    maxLeverage: new BigNumber(100),
    spread: new BigNumber(1.0),
    commType: 'percent',
    commRate: new BigNumber(0.003),
    swapLong: new BigNumber(-0.50),
    swapShort: new BigNumber(0.20),
    nights: new BigNumber(0),
    wedEnabled: false,
    safetyBufferR: new BigNumber(1),
    entryPrice: new BigNumber("1.08500"),
    stopLoss: new BigNumber("1.08200"),
    // Multiple take-profits (partial exits). Disabled → single TP as before.
    multiTpEnabled: false,
    tpCount: 3,
    tpMode: 'percent',
    tpExits: defaultTpExits(),
};

const defaultFeeBreakdown = {
    spreadCost: '--',
    entryComm: '--',
    exitComm: '--',
    swapCost: '--',
    totalRoundTurn: '--',
    spreadDetail: '',
    entryCommDetail: '',
    exitCommDetail: '',
    swapDetail: '',
};

const defaultMetrics = {
    positionSize: '--',
    quantity: '--',
    lots: '--',
    leverage: '--',
    margin: '--',
    effectiveTake: '--',
    nominalTake: '--',
    effectiveStop: '--',
    nominalStop: '--',
    effectiveRR: '--',
    friction: '--',
    nominalRR: '--',
    optimizedRR: '--',
    priceLevels: {
        liquidationPrice: '--',
        breakEven: '--',
        takeProfit: '--',
        optimizedTakeProfit: '--',
        stopLoss: '--'
    },
    tradingFees: {
        entryFee: '--',
        tpFee: '--',
        slFee: '--'
    },
    totalFees: {
        takeProfitFees: '--',
        stopLossFees: '--'
    },
    feeBreakdown: { ...defaultFeeBreakdown },
    multiExit: null,
};

// Returns pip value for 1 unit in USD
const getPipValuePerUnit = (pairSpec: PairSpec, entryPrice: BigNumber): BigNumber => {
    const pipSize = new BigNumber(pairSpec.pipSize);
    return pairSpec.type === 'usd_base'
        ? pipSize.dividedBy(entryPrice)
        : pipSize; // usd_quote and cross (cross is approximation)
};

const serializeValues = (values: typeof defaultValues) => {
    const serialized: Record<string, any> = {};
    Object.entries(values).forEach(([key, value]) => {
        if (key === 'tpExits' && Array.isArray(value)) {
            serialized[key] = value.map((e: any) => ({
                alloc: e.alloc instanceof BigNumber ? e.alloc.toString() : e.alloc,
                rMultiple: e.rMultiple instanceof BigNumber ? e.rMultiple.toString() : e.rMultiple,
            }));
        } else if (value instanceof BigNumber) {
            serialized[key] = value.toString();
        } else {
            serialized[key] = value;
        }
    });
    return JSON.stringify(serialized);
};

const STRING_FIELDS = new Set(['riskUnit', 'pair', 'commType', 'tpMode']);
const BOOLEAN_FIELDS = new Set(['wedEnabled', 'multiTpEnabled']);

const deserializeValues = (serializedData: string) => {
    try {
        const parsed = JSON.parse(serializedData);
        const deserialized: any = { ...defaultValues };
        Object.entries(parsed).forEach(([key, value]) => {
            if (key === 'updatedAt' || !(key in defaultValues)) return;
            if (key === 'tpExits') {
                deserialized[key] = Array.isArray(value)
                    ? value.map((e: any) => ({
                        alloc: new BigNumber(e.alloc),
                        rMultiple: new BigNumber(e.rMultiple),
                    }))
                    : defaultTpExits();
            } else if (key === 'tpCount') {
                deserialized[key] = value;
            } else if (STRING_FIELDS.has(key)) {
                deserialized[key] = value as string;
            } else if (BOOLEAN_FIELDS.has(key)) {
                deserialized[key] = Boolean(value);
            } else if (typeof value === 'string') {
                try {
                    deserialized[key] = new BigNumber(value);
                } catch (e) {
                    console.warn(`[Forex] Failed to parse BigNumber for ${key}:`, e);
                }
            } else if (typeof value === 'number') {
                deserialized[key] = new BigNumber(value);
            } else {
                deserialized[key] = value;
            }
        });
        return deserialized;
    } catch (e) {
        console.warn('[Forex] Failed to deserialize values:', e);
        return defaultValues;
    }
};

const loadFromLocalStorage = (): typeof defaultValues => {
    try {
        const stored = localStorage.getItem(LOCALSTORAGE_KEY);
        if (stored) {
            return deserializeValues(stored);
        }
    } catch (e) {
        console.warn('[Forex] Failed to load from localStorage:', e);
    }
    return defaultValues;
};

const saveToLocalStorage = (values: typeof defaultValues) => {
    try {
        const serialized = serializeValues(values);
        const settingsWithTimestamp = {
            ...JSON.parse(serialized),
            updatedAt: new Date().toISOString()
        };
        localStorage.setItem(LOCALSTORAGE_KEY, JSON.stringify(settingsWithTimestamp));
    } catch (e) {
        console.warn('[Forex] Failed to save to localStorage:', e);
    }
};

export const useForexCalculator = () => {
    const [values, setValues] = useState(() => loadFromLocalStorage());
    const [metrics, setMetrics] = useState(defaultMetrics);
    const [isCalculating, setIsCalculating] = useState(false);
    const [extremePriceField, setExtremePriceField] = useState(null);
    const [validationErrors, setValidationErrors] = useState({});
    const [apiSyncStatus, setApiSyncStatus] = useState('idle');
    const lastRequestId = useRef(0);
    const settingsLoadedFromApi = useRef(false);
    const initialLoadComplete = useRef(false);

    // Current pair spec
    const pairSpec = useMemo(() => {
        return FOREX_PAIRS[values.pair] || FOREX_PAIRS[DEFAULT_PAIR];
    }, [values.pair]);

    const debouncedSaveToLocalStorage = useMemo(
        () => debounce((valuesToSave: typeof defaultValues) => {
            saveToLocalStorage(valuesToSave);
        }, LOCALSTORAGE_DEBOUNCE),
        []
    );

    const debouncedSyncToApi = useMemo(
        () => debounce(async (valuesToSync: typeof defaultValues) => {
            const canSync = await UserSettingsApi.canSyncSettings();
            if (!canSync) return;
            try {
                setApiSyncStatus('syncing');
                const serializedSettings = serializeValues(valuesToSync);
                const success = await UserSettingsApi.updateUserSettings(
                    JSON.parse(serializedSettings)
                );
                setApiSyncStatus(success ? 'idle' : 'error');
                if (!success) setTimeout(() => setApiSyncStatus('idle'), 5000);
            } catch (error) {
                console.error('[Forex] Error syncing to API:', error);
                setApiSyncStatus('error');
                setTimeout(() => setApiSyncStatus('idle'), 5000);
            }
        }, API_SYNC_DEBOUNCE),
        []
    );

    const loadSettingsFromApi = useCallback(async () => {
        if (settingsLoadedFromApi.current) return;
        try {
            setApiSyncStatus('loading');
            const apiSettings = await UserSettingsApi.getUserSettings();
            if (apiSettings && apiSettings.forex_calculator_settings) {
                const currentLocalStorage = localStorage.getItem(LOCALSTORAGE_KEY);
                let shouldUpdate = true;
                if (currentLocalStorage) {
                    try {
                        const currentParsed = JSON.parse(currentLocalStorage);
                        const apiTimestamp = apiSettings.updatedAt ? new Date(apiSettings.updatedAt).getTime() : 0;
                        const localTimestamp = currentParsed.updatedAt ? new Date(currentParsed.updatedAt).getTime() : 0;
                        shouldUpdate = !localTimestamp || apiTimestamp > localTimestamp;
                    } catch (e) {
                        shouldUpdate = true;
                    }
                }
                if (shouldUpdate) {
                    const deserializedSettings = deserializeValues(JSON.stringify(apiSettings.forex_calculator_settings));
                    setValues(deserializedSettings);
                    const settingsWithTimestamp = {
                        ...apiSettings.forex_calculator_settings,
                        updatedAt: apiSettings.updatedAt || new Date().toISOString()
                    };
                    localStorage.setItem(LOCALSTORAGE_KEY, JSON.stringify(settingsWithTimestamp));
                }
            }
            setApiSyncStatus('idle');
        } catch (error) {
            console.error('[Forex] Error loading from API:', error);
            setApiSyncStatus('error');
            setTimeout(() => setApiSyncStatus('idle'), 5000);
        } finally {
            settingsLoadedFromApi.current = true;
            initialLoadComplete.current = true;
        }
    }, []);

    useEffect(() => {
        const timer = setTimeout(() => { loadSettingsFromApi(); }, 100);
        return () => clearTimeout(timer);
    }, [loadSettingsFromApi]);

    useEffect(() => {
        debouncedSaveToLocalStorage(values);
        if (initialLoadComplete.current) {
            debouncedSyncToApi(values);
        }
        return () => {
            debouncedSaveToLocalStorage.cancel();
            debouncedSyncToApi.cancel();
        };
    }, [values, debouncedSaveToLocalStorage, debouncedSyncToApi]);

    const checkMinValueErrors = useCallback((currentValues) => {
        const errors = {};
        if (currentValues.risk && currentValues.risk.isLessThan(MIN_VALUES.risk)) errors.risk = true;
        if (currentValues.nominalRR && currentValues.nominalRR.isLessThan(MIN_VALUES.nominalRR)) errors.nominalRR = true;
        if (currentValues.accountBalance && currentValues.accountBalance.isLessThan(MIN_VALUES.accountBalance)) errors.accountBalance = true;
        if (currentValues.maxLeverage && currentValues.maxLeverage.isLessThan(MIN_VALUES.maxLeverage)) errors.maxLeverage = true;
        if (currentValues.safetyBufferR && currentValues.safetyBufferR.isLessThan(MIN_VALUES.safetyBufferR)) errors.safetyBufferR = true;
        return errors;
    }, []);

    useEffect(() => {
        const newErrors = checkMinValueErrors(values);
        setValidationErrors(newErrors);
    }, [values, checkMinValueErrors]);

    const validateField = useCallback((field, value) => {
        if (!value || value.isLessThanOrEqualTo(0)) return true;
        return false;
    }, []);

    const isValidPriceRange = (entryPrice, stopLoss, sourceField = null) => {
        if (!entryPrice || !stopLoss || entryPrice.isZero() || stopLoss.isZero()) return false;

        const bigger = BigNumber.max(entryPrice, stopLoss);
        const smaller = BigNumber.min(entryPrice, stopLoss);
        const priceRatio = bigger.dividedBy(smaller);

        if (priceRatio.gt(20)) {
            if (sourceField) {
                setExtremePriceField(sourceField === 'entryPrice' ? 'stopLoss' : 'entryPrice');
            } else {
                setExtremePriceField(entryPrice.gt(stopLoss) ? 'entryPrice' : 'stopLoss');
            }
            return false;
        }

        const priceDiff = entryPrice.minus(stopLoss).abs();
        const relativeDiff = priceDiff.dividedBy(BigNumber.max(entryPrice, stopLoss));
        if (relativeDiff.lt(0.0001)) {
            if (sourceField) {
                setExtremePriceField(sourceField === 'entryPrice' ? 'stopLoss' : 'entryPrice');
            } else {
                setExtremePriceField(entryPrice.gt(stopLoss) ? 'entryPrice' : 'stopLoss');
            }
            return false;
        }

        setExtremePriceField(null);
        return true;
    };

    const updateValues = useCallback((newValues) => {
        if (typeof newValues === 'function') {
            setValues(prevValues => {
                const actualNewValues = newValues(prevValues);
                const sanitizedValues = { ...actualNewValues };
                let hasChanges = false;
                if (sanitizedValues.nominalRR && sanitizedValues.nominalRR.isLessThan?.(MIN_VALUES.nominalRR)) {
                    sanitizedValues.nominalRR = MIN_VALUES.nominalRR; hasChanges = true;
                }
                if (sanitizedValues.risk && sanitizedValues.risk.isLessThan?.(MIN_VALUES.risk)) {
                    sanitizedValues.risk = MIN_VALUES.risk; hasChanges = true;
                }
                if (sanitizedValues.accountBalance && sanitizedValues.accountBalance.isLessThan?.(MIN_VALUES.accountBalance)) {
                    sanitizedValues.accountBalance = MIN_VALUES.accountBalance; hasChanges = true;
                }
                if (sanitizedValues.maxLeverage && sanitizedValues.maxLeverage.isLessThan?.(MIN_VALUES.maxLeverage)) {
                    sanitizedValues.maxLeverage = MIN_VALUES.maxLeverage; hasChanges = true;
                }
                if (sanitizedValues.safetyBufferR && sanitizedValues.safetyBufferR.isLessThan?.(MIN_VALUES.safetyBufferR)) {
                    sanitizedValues.safetyBufferR = MIN_VALUES.safetyBufferR; hasChanges = true;
                }
                return hasChanges ? sanitizedValues : actualNewValues;
            });
            return true;
        }

        if (!newValues) return false;

        const errors = {};
        Object.entries(newValues).forEach(([field, value]) => {
            if (value instanceof BigNumber && validateField(field, value)) {
                errors[field] = true;
            }
        });

        const changedField = Object.keys(newValues).find(key => {
            const isOldBigNumber = values[key]?.constructor?.name === 'BigNumber';
            const isNewBigNumber = newValues[key]?.constructor?.name === 'BigNumber';
            if (isOldBigNumber && isNewBigNumber) return !values[key].isEqualTo(newValues[key]);
            return values[key] !== newValues[key];
        });

        if (newValues.entryPrice && newValues.stopLoss) {
            isValidPriceRange(newValues.entryPrice, newValues.stopLoss, changedField);
        }

        setValues(newValues);
        return Object.keys(errors).length === 0;
    }, [validateField, values]);

    const computeFeeBreakdown = useCallback((quantity: BigNumber, originalEntryPrice: BigNumber, currentValues: typeof defaultValues, currentPairSpec: PairSpec) => {
        const isUsdBase = currentPairSpec.type === 'usd_base';

        // Pip value for full position
        const pipValuePerUnit = getPipValuePerUnit(currentPairSpec, originalEntryPrice);
        const pipValue = pipValuePerUnit.times(quantity);

        const spreadCost = currentValues.spread.times(pipValue);
        const lots = quantity.dividedBy(currentPairSpec.lotSize);

        // Position size in USD
        const posSize = isUsdBase ? quantity : quantity.times(originalEntryPrice);

        let entryComm: BigNumber, exitComm: BigNumber;
        if (currentValues.commType === 'percent') {
            entryComm = posSize.times(currentValues.commRate).dividedBy(100);
            exitComm = entryComm;
        } else {
            // per_lot: commRate is round-turn $/lot, so divide by 2 for each side
            entryComm = currentValues.commRate.times(lots).dividedBy(2);
            exitComm = entryComm;
        }

        // Swap
        const isLong = currentValues.entryPrice.gt(currentValues.stopLoss);
        const swapRate = isLong ? currentValues.swapLong : currentValues.swapShort;
        const effectiveNights = currentValues.wedEnabled
            ? currentValues.nights.plus(2)
            : currentValues.nights;
        const swapCost = swapRate.times(lots).times(effectiveNights);

        const totalRoundTurn = spreadCost.plus(entryComm).plus(exitComm).plus(swapCost);

        // Detail strings
        const fmtOpts = { decimalSeparator: '.', groupSeparator: ',', groupSize: 3 };
        const spreadDetail = `${currentValues.spread.toFixed(1)} pip × $${pipValue.toFormat(2, fmtOpts)}/pip`;
        let entryCommDetail: string, exitCommDetail: string;
        if (currentValues.commType === 'percent') {
            entryCommDetail = `${currentValues.commRate.toString()}% × $${posSize.toFormat(2, fmtOpts)}`;
            exitCommDetail = entryCommDetail;
        } else {
            entryCommDetail = `$${currentValues.commRate.toString()}/lot RT × ${lots.toFormat(2, fmtOpts)} lots ÷ 2`;
            exitCommDetail = entryCommDetail;
        }

        let swapDetail: string;
        if (effectiveNights.isZero()) {
            swapDetail = '0 nights';
        } else if (currentValues.wedEnabled && currentValues.nights.gt(0)) {
            swapDetail = `${currentValues.nights.toString()} nights + Wed ×3`;
        } else if (currentValues.wedEnabled) {
            swapDetail = 'Wed ×3 (2 nights)';
        } else {
            swapDetail = `${currentValues.nights.toString()} nights`;
        }

        return {
            spreadCost: `$${spreadCost.toFormat(2, fmtOpts)}`,
            entryComm: `$${entryComm.toFormat(2, fmtOpts)}`,
            exitComm: `$${exitComm.toFormat(2, fmtOpts)}`,
            swapCost: swapCost.isZero() ? '$0.00' : `${swapCost.isNegative() ? '-' : ''}$${swapCost.abs().toFormat(2, fmtOpts)}`,
            totalRoundTurn: totalRoundTurn.isNegative()
                ? `-$${totalRoundTurn.abs().toFormat(2, fmtOpts)}`
                : `$${totalRoundTurn.toFormat(2, fmtOpts)}`,
            spreadDetail,
            entryCommDetail,
            exitCommDetail,
            swapDetail,
            swapIsNegative: swapCost.isNegative(),
            swapIsIncome: swapCost.isGreaterThan(0),
        };
    }, []);

    const calculateMetrics = useCallback(async () => {
        const currentRequestId = lastRequestId.current + 1;
        lastRequestId.current = currentRequestId;

        if (!isValidPriceRange(values.entryPrice, values.stopLoss)) {
            setMetrics(defaultMetrics);
            return;
        }

        try {
            setIsCalculating(true);

            const isUsdBase = pairSpec.type === 'usd_base';
            const pipValuePerUnit = getPipValuePerUnit(pairSpec, values.entryPrice);
            const normalizedEntry = isUsdBase ? new BigNumber(1) : values.entryPrice;

            // Spread -> entry fee %
            const spreadPct = values.spread.times(pipValuePerUnit)
                .dividedBy(normalizedEntry).times(100);

            // Commission -> fee % (each side)
            let commPct: BigNumber;
            if (values.commType === 'percent') {
                commPct = values.commRate;
            } else {
                commPct = values.commRate.dividedBy(2)
                    .dividedBy(pairSpec.lotSize)
                    .dividedBy(normalizedEntry).times(100);
            }

            // Price normalization for usd_base pairs
            const sentEntry = isUsdBase ? '1' : values.entryPrice.toString();
            const sentStop = isUsdBase
                ? values.stopLoss.dividedBy(values.entryPrice).toString()
                : values.stopLoss.toString();

            const requestBody: CalculatorRequest = {
                entryPrice: sentEntry,
                stopLoss: sentStop,
                risk: values.risk.toString(),
                accountBalance: values.accountBalance.toString(),
                riskUnit: values.riskUnit as RiskUnit,
                nominalRR: values.nominalRR.toString(),
                fees: {
                    entry: spreadPct.plus(commPct).toString(),
                    exit: commPct.toString(),
                },
                margin: {
                    safety: values.safetyBufferR.toString(),
                    maxLeverage: values.maxLeverage ? values.maxLeverage.toString() : "100"
                }
            };

            let normalizedRiskUnit = values.riskUnit;
            if (values.riskUnit === '%' || values.riskUnit === 'percentage') normalizedRiskUnit = 'percent';
            else if (values.riskUnit === '$' || values.riskUnit === 'dollar') normalizedRiskUnit = 'fixed';
            else if (!['percent', 'fixed'].includes(values.riskUnit)) normalizedRiskUnit = 'fixed';
            requestBody.riskUnit = normalizedRiskUnit;

            const data = await RustCalculatorApi.calculate(requestBody);

            if (currentRequestId !== lastRequestId.current) return;

            if (!data.priceLevels || !data.tradingFees || !data.totalFees) {
                throw new Error('Invalid response structure from server');
            }

            // Floor quantity to integer units so real risk never exceeds user-specified risk
            const rawQuantity = new BigNumber(data.quantity);
            const quantityBN = rawQuantity.integerValue(BigNumber.ROUND_DOWN);
            const lotsBN = quantityBN.dividedBy(pairSpec.lotSize);

            // Reconstruct price levels for usd_base pairs
            // Backend rounds to 2 decimals, losing precision when entry=1
            // So we compute from monetary values + known entry/stop
            if (isUsdBase) {
                const entry = values.entryPrice;
                const stop = values.stopLoss;
                const isShort = stop.gt(entry);
                const distance = stop.minus(entry).abs();

                // Stop: exact input value
                data.priceLevels.stopLoss = stop.toString();

                // Nominal TP: naive TP = entry ± distance × inputRR (no fee adjustment)
                data.priceLevels.takeProfit = isShort
                    ? entry.minus(distance.times(values.nominalRR)).toString()
                    : entry.plus(distance.times(values.nominalRR)).toString();

                // Optimized TP: from nominalTake (gross profit at optimized TP)
                // nominalTake = qty × |entry_norm - tp_norm|, so tpDist_orig = nominalTake / qty × entry
                const nomTake = new BigNumber(data.nominalTake);
                const optTpDist = nomTake.dividedBy(quantityBN).times(entry);
                data.priceLevels.optimizedTakeProfit = isShort
                    ? entry.minus(optTpDist).toString()
                    : entry.plus(optTpDist).toString();

                // Break-even: entryFee / qty × entry = price shift to cover entry costs
                const entryFeeAbs = new BigNumber(data.tradingFees.entryFee);
                const beDist = entryFeeAbs.dividedBy(quantityBN).times(entry);
                data.priceLevels.breakEven = isShort
                    ? entry.plus(beDist).toString()
                    : entry.minus(beDist).toString();

                // Liquidation: backend value has enough precision (e.g. 1.03 → 153.985)
                const liqNorm = new BigNumber(data.priceLevels.liquidationPrice);
                data.priceLevels.liquidationPrice = liqNorm.times(entry).toString();
            }

            // Compute fee breakdown
            const feeBreakdown = computeFeeBreakdown(quantityBN, values.entryPrice, values, pairSpec);

            const formattedData = {
                ...data,
                positionSize: pairSpec.type === 'usd_base'
                    ? quantityBN
                    : quantityBN.times(values.entryPrice),
                quantity: quantityBN,
                lots: lotsBN,
                leverage: new BigNumber(data.leverage),
                margin: new BigNumber(data.margin),
                effectiveTake: new BigNumber(data.effectiveTake),
                nominalTake: new BigNumber(data.nominalTake),
                effectiveStop: new BigNumber(data.effectiveStop),
                nominalStop: new BigNumber(data.nominalStop),
                effectiveRR: new BigNumber(data.effectiveRR),
                friction: new BigNumber(data.friction),
                nominalRR: new BigNumber(data.nominalRR),
                optimizedRR: new BigNumber(data.optimizedRR),
                priceLevels: {
                    liquidationPrice: data.priceLevels.liquidationPrice,
                    breakEven: data.priceLevels.breakEven,
                    takeProfit: data.priceLevels.takeProfit,
                    optimizedTakeProfit: data.priceLevels.optimizedTakeProfit,
                    stopLoss: data.priceLevels.stopLoss
                },
                tradingFees: {
                    entryFee: data.tradingFees.entryFee === '--' ? new BigNumber(0) : new BigNumber(data.tradingFees.entryFee),
                    tpFee: data.tradingFees.tpFee === '--' ? new BigNumber(0) : new BigNumber(data.tradingFees.tpFee),
                    slFee: data.tradingFees.slFee === '--' ? new BigNumber(0) : new BigNumber(data.tradingFees.slFee),
                },
                totalFees: {
                    takeProfitFees: data.totalFees.takeProfitFees === '--' ? new BigNumber(0) : new BigNumber(data.totalFees.takeProfitFees),
                    stopLossFees: data.totalFees.stopLossFees === '--' ? new BigNumber(0) : new BigNumber(data.totalFees.stopLossFees),
                },
                feeBreakdown,
            };

            // priceDecimals intentionally omitted — computeMultiExits' own default
            // (8dp) preserves full precision. Deriving it from breakEven's incidental
            // decimal count used to silently truncate target prices below what's
            // needed to reproduce the stated R-multiple.
            const multiExit = values.multiTpEnabled
                ? computeMultiExits({
                    entryPrice: values.entryPrice,
                    stopDistance: values.entryPrice.minus(values.stopLoss).abs(),
                    tpPathFee: formattedData.totalFees.takeProfitFees,
                    exits: values.tpExits,
                    mode: values.tpMode,
                    quantity: formattedData.quantity,
                    riskAmount: formattedData.effectiveStop,
                    breakEven: formattedData.priceLevels.breakEven,
                    optimizedTakeProfit: formattedData.priceLevels.optimizedTakeProfit,
                    nominalRR: formattedData.nominalRR,
                    side: values.entryPrice.gt(values.stopLoss) ? 'long' : 'short',
                })
                : null;

            setMetrics({ ...formattedData, multiExit });
        } catch (err) {
            console.error('[Forex] Calculation error:', err);
            setMetrics(defaultMetrics);
        } finally {
            if (currentRequestId === lastRequestId.current) {
                setIsCalculating(false);
            }
        }
    }, [values, pairSpec, computeFeeBreakdown]);

    const debouncedCalculateMetrics = useMemo(
        () => debounce(calculateMetrics, CALCULATION_DELAY),
        [calculateMetrics]
    );

    useEffect(() => {
        debouncedCalculateMetrics();
        return () => debouncedCalculateMetrics.cancel();
    }, [debouncedCalculateMetrics]);

    return {
        values,
        setValues: updateValues,
        metrics,
        pairSpec,
        calculateMetrics: debouncedCalculateMetrics,
        isCalculating,
        validationErrors,
        extremePriceField,
        apiSyncStatus,
        loadSettingsFromApi,
    };
};
